import TimeAgo, { LabelStyleName, Step, Style } from 'javascript-time-ago';
import moment from 'moment';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useForceRefresh } from '../../hooks/useForceRefresh';
import i18n from '../../locale';
import { StyledTextTooltip } from './RelativeDate.styles';

const SET_TIMEOUT_MAX_SAFE_INTERVAL = 2147483647;

interface RelativeDateProps {
  date: Date | string;
  label?: LabelStyleName;
  hideTooltip?: boolean;
  alwaysPast?: boolean;
}

const SafeRelativeDate = ({ date, label = 'long', hideTooltip = false, alwaysPast = true }: RelativeDateProps) => {
  const { t } = useTranslation();
  const forceRefresh = useForceRefresh();

  const steps: Step[] = [
    {
      minTime: 0,
      formatAs: 'now',
    },
    {
      // 44 seconds
      minTime: 44,
      formatAs: 'minute',
    },
    {
      // 1 hour
      minTime: 60 * 60,
      formatAs: 'hour',
    },
    {
      // 24 hours
      minTime: 24 * 60 * 60,
      formatAs: 'day',
    },
    {
      // 7 days
      minTime: 7 * 24 * 60 * 60,
      formatAs: 'week',
    },
    {
      // 4 weeks
      minTime: 4 * 7 * 24 * 60 * 60,
      formatAs: 'month',
    },
    {
      // 12 months
      minTime: 365 * 24 * 60 * 60,
      formatAs: 'year',
    },
  ];

  const customStyle: Style = {
    steps,
    labels: label,
  };

  const timeAgo = new TimeAgo(i18n.language);

  const isDate = date instanceof Date;
  let dateToFormat = isDate ? date : new Date(date);

  // If difference is in the future, show new Date() to avoid displaying "in a moment".
  if (alwaysPast && moment(dateToFormat).isAfter(new Date())) {
    dateToFormat = new Date();
  }

  const [formattedDate, timeToNextUpdate] = timeAgo.format(dateToFormat, customStyle, {
    round: 'round',
    getTimeToNextUpdate: true,
  });

  const getSafeTimeoutInterval = (interval: number) => Math.min(interval, SET_TIMEOUT_MAX_SAFE_INTERVAL);

  useEffect(() => {
    const timer = setTimeout(() => {
      forceRefresh();
    }, getSafeTimeoutInterval(timeToNextUpdate ? Number(timeToNextUpdate) : 60 * 1000));
    return () => clearTimeout(timer);
  }, [timeToNextUpdate, forceRefresh]);

  return hideTooltip ? (
    <>{formattedDate}</>
  ) : (
    <StyledTextTooltip $minWidth="max-content" tooltipText={t('dateTooltip', { val: moment(dateToFormat) })}>
      {formattedDate}
    </StyledTextTooltip>
  );
};

const RelativeDate = (props: RelativeDateProps) => {
  return props.date ? <SafeRelativeDate {...props} /> : <></>;
};

export default RelativeDate;
